import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/navigation/Sidebar';
import QuickActions from '../../components/navigation/QuickActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MarketingMetrics from './components/MarketingMetrics';
import OnlineBookingManagement from './components/OnlineBookingManagement';
import AutomatedReminders from './components/AutomatedReminders';
import ReputationManagement from './components/ReputationManagement';
import CommunicationCampaigns from './components/CommunicationCampaigns';
import { marketingService } from '../../services/marketingService';

const DentalPracticeMarketingHub = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [dateRange, setDateRange] = useState('month');
  const [selectedView, setSelectedView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metrics, setMetrics] = useState([]);

  const viewOptions = [
    { value: 'overview', label: 'Overview' },
    { value: 'bookings', label: 'Online Bookings' },
    { value: 'reminders', label: 'Automated Reminders' },
    { value: 'reputation', label: 'Reputation Management' },
    { value: 'campaigns', label: 'Marketing Campaigns' }
  ];

  useEffect(() => {
    loadMetrics();
  }, [dateRange]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const data = await marketingService?.getMetrics();
      setMetrics(data);
    } catch (err) {
      setError(err?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    console.log('Exporting marketing report...');
  };

  const handleRefreshData = () => {
    loadMetrics();
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden pt-16">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-card border-b border-border px-4 md:px-6 py-4 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
                <Icon name="TrendingUp" size={28} className="text-primary" />
                Dental Practice Marketing Hub
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Patient acquisition, retention strategies, and reputation management
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={handleRefreshData}
              >
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportReport}
              >
                <span className="hidden sm:inline">Export Report</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-[1600px] mx-auto space-y-6">
            <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <Select
                    options={viewOptions}
                    value={selectedView}
                    onChange={setSelectedView}
                    placeholder="Select View"
                    className="w-full sm:w-64"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDateRange('week')}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      dateRange === 'week' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    onClick={() => setDateRange('month')}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      dateRange === 'month' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => setDateRange('quarter')}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      dateRange === 'quarter' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Quarter
                  </button>
                  <button
                    onClick={() => setDateRange('year')}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      dateRange === 'year' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Year
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-error/10 border border-error rounded-lg p-4">
                <p className="text-error text-sm">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {selectedView === 'overview' && (
                  <>
                    <MarketingMetrics dateRange={dateRange} metrics={metrics} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <OnlineBookingManagement />
                      <AutomatedReminders />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <ReputationManagement />
                      <CommunicationCampaigns />
                    </div>
                  </>
                )}

                {selectedView === 'bookings' && <OnlineBookingManagement expanded />}
                {selectedView === 'reminders' && <AutomatedReminders expanded />}
                {selectedView === 'reputation' && <ReputationManagement expanded />}
                {selectedView === 'campaigns' && <CommunicationCampaigns expanded />}
              </>
            )}
          </div>
        </main>

        <QuickActions />
      </div>
    </div>
  );
};

export default DentalPracticeMarketingHub;